import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, ButtonGroup, ButtonGroupProps, CircularProgress, Popover, Tooltip } from '@mui/material';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useJoinCampaignOffer, useJoinCampaignsLoader } from '../../controllers';
import { JoinCampaignOption } from '../../types';

interface SaveAndStartJoinCampaignButtonProps
  extends Pick<ButtonGroupProps, 'variant' | 'color' | 'size' | 'sx'> {
  /** What the button does, e.g. "Update"; the button the arrow shows reads "Update and start Join Campaign". */
  label: string;
  /** The main button's content when it differs from the label, e.g. a progress indicator. */
  children?: ReactNode;
  disabled?: boolean;
  /** Width of the main button, the arrow comes on top of it. */
  mainButtonWidth?: number | string;
  /** The groups the contacts are being added to, whose join campaigns can be started for them. */
  groupIds: string[];
  /** Every contact is in the default group, so its join campaigns can be started as well. */
  includeDefaultGroup?: boolean;
  /** Leaves only the save button, for when the form already knows there is no campaign to start. */
  hideStartOption?: boolean;
  /**
   * Saves. Resolves to the contacts added to the groups, or to nothing when the save failed. When the user chose
   * to start a join campaign, the start join campaign dialog then opens for those contacts.
   */
  onSave: () => Promise<string[] | undefined>;
}

/**
 * A save button with an arrow that offers to save and start a join campaign. The campaigns are loaded when the
 * arrow is clicked, so they match what the form holds at that moment.
 */
export const SaveAndStartJoinCampaignButton: FC<SaveAndStartJoinCampaignButtonProps> = ({
  label,
  children,
  disabled,
  mainButtonWidth,
  groupIds,
  includeDefaultGroup = false,
  hideStartOption = false,
  onSave,
  variant = 'contained',
  color,
  size,
  sx,
}) => {
  const loadJoinCampaigns = useJoinCampaignsLoader();
  const offerJoinCampaigns = useJoinCampaignOffer();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  // null while loading.
  const [campaigns, setCampaigns] = useState<JoinCampaignOption[] | null>(null);
  // Only the latest load counts: the option may be closed and opened again before an earlier load ends.
  const loadId = useRef(0);

  const close = () => {
    loadId.current++;
    setIsOpen(false);
  };

  useEffect(() => {
    if (disabled || hideStartOption) close();
  }, [disabled, hideStartOption]);

  const open = async () => {
    const id = ++loadId.current;
    setCampaigns(null);
    setIsOpen(true);
    const loaded = await loadJoinCampaigns(groupIds, includeDefaultGroup).catch(() => []);
    if (id === loadId.current) setCampaigns(loaded);
  };

  const saveAndStart = async () => {
    if (!campaigns?.length) return;
    const campaignsToStart = campaigns;
    close();
    const contactIds = await onSave();
    if (contactIds?.length) offerJoinCampaigns(contactIds, campaignsToStart);
  };

  const saveAndStartButton = (
    <Button
      variant={variant}
      color={color}
      size={size}
      disabled={!campaigns?.length}
      onClick={saveAndStart}
      endIcon={campaigns === null && <CircularProgress size={16} color="inherit" aria-label="Loading campaigns" />}
      // As wide as the button it drops from at least; the label is longer, so it may be wider.
      sx={{ minWidth: anchorRef.current?.offsetWidth, whiteSpace: 'nowrap' }}
    >
      {label} and start Join Campaign
    </Button>
  );

  return (
    <>
      <ButtonGroup ref={anchorRef} variant={variant} color={color} size={size} disabled={disabled} sx={sx}>
        <Button onClick={() => onSave()} sx={{ width: mainButtonWidth }}>
          {children ?? label}
        </Button>
        {!hideStartOption && (
          <Button
            size="small"
            aria-label={`More ${label} options`}
            aria-haspopup="true"
            aria-expanded={isOpen}
            onClick={open}
          >
            <ArrowDropDownIcon />
          </Button>
        )}
      </ButtonGroup>
      {/* A popover rather than a menu, so the option is a button like the one above it. */}
      <Popover
        anchorEl={anchorRef.current}
        open={isOpen}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { mt: 0.5, background: 'transparent', boxShadow: 'none', overflow: 'visible' } },
        }}
      >
        {campaigns?.length === 0 ? (
          // A disabled button gets no pointer events, so the tooltip hangs on a wrapper.
          <Tooltip title="No join campaigns to start">
            <span style={{ display: 'inline-block' }}>{saveAndStartButton}</span>
          </Tooltip>
        ) : (
          saveAndStartButton
        )}
      </Popover>
    </>
  );
};
